import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2, Wrench } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { loadFleet } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [plan, setPlan] = useState("");

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setPlan(data.plan); await loadFleet(); setState("success"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  const Box = ({ icon: Icon, color, title, sub, children }) => (
    <div className="card p-10 max-w-md w-full text-center" data-testid="payment-result">
      <Icon className="w-14 h-14 mx-auto mb-4" style={{ color }} />
      <h1 className="font-head font-900 text-4xl uppercase tracking-tight">{title}</h1>
      <p className="font-body text-[#94A3B8] mt-2">{sub}</p>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0D0E11] flex flex-col items-center justify-center p-6">
      <Link to="/dashboard" className="flex items-center gap-2.5 mb-8">
        <div className="w-8 h-8 bg-[#FFB800] flex items-center justify-center"><Wrench className="w-5 h-5 text-black" strokeWidth={2.5} /></div>
        <span className="font-head font-900 text-2xl tracking-tight">IRONLOG</span>
      </Link>
      {state === "checking" && <Box icon={Loader2} color="#FFB800" title="Confirming…" sub="Verifying your payment with Stripe." />}
      {state === "success" && (
        <Box icon={CheckCircle2} color="#00E676" title="Plan Active" sub="Your fleet's subscription is live. Machine capacity updated.">
          <Link to="/dashboard" className="btn-signal inline-flex h-12 px-6 font-head items-center mt-6" data-testid="payment-continue">Go to Dashboard</Link>
        </Box>
      )}
      {(state === "failed" || state === "timeout") && (
        <Box icon={XCircle} color="#FF3B30" title={state === "timeout" ? "Still Processing" : "Payment Issue"} sub={state === "timeout" ? "Taking longer than expected. Check billing shortly." : "We couldn't confirm the payment."}>
          <Link to="/billing" className="btn-ghost inline-flex h-12 px-6 font-head items-center mt-6">Back to Billing</Link>
        </Box>
      )}
    </div>
  );
}
